import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faArrowRight, faArrowLeft, faSpinner, faPlus, faTrash, faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import customersService from '../../services/customersService';
import locationsService from '../../services/locationsService';
import productSizesService from '../../services/productSizesServices';
import inquiriesService from '../../services/inquiriesService';
import warehouseService from '../../services/warehouseService';
import validateUtils from '../../utils/validate';
import { normalizeUnit } from '../../utils/units';
import SearchableSelect from '../common/SearchableSelect';
import {
  buildDeliveryLocationOptions,
  buildCustomerAddressDraft,
  isSyntheticLocationValue,
} from '../../utils/deliveryLocationOptions';
import useCustomerDeliveryLocation from '../../hooks/useCustomerDeliveryLocation';

import AddLocationModal from '../locations/AddLocationModal';
import CustomerWarehouseForm from '../customers/CustomerWarehouseForm';
import { useCoilSettings } from '../../context/CoilSettingsContext';

// defaultNewCustomer will be created inside the component so it can use localized strings

// Small helper to format price-like numbers
const sanitizeNumber = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const s = String(v).replace(/[^0-9.]/g, '');
  return s;
};

// Helper to create an empty item
const emptyItem = () => ({ product_size_id: '', units: 'kg', length: '6', quantity: '', quantity_in_pieces: '', cut_pieces: '', price: '', remark: '', coil_tracked: false });

// Unit vocabulary: staff uses 'm', portal/BE uses 'meters'; treat both as
// length-based. 'kg' is weight-based; 'pieces'/'pcs' are already piece counts.
const isLengthUnit = (u) => u === 'm' || u === 'meters';
const isWeightUnit = (u) => u === 'kg';



// Helper to get tomorrow's date in YYYY-MM-DD format
const getTomorrowDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const y = tomorrow.getFullYear();
  const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const d = String(tomorrow.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const InquiryForm = ({ isOpen, onClose, onSubmit, inquiry = null, inquiryId = null, loading = false, isConverting = false }) => {
  const { t } = useTranslation();
  const { coilTrackingEnabled } = useCoilSettings();
  const strings = t('inquiriesModule.inquiriesForm', { returnObjects: true });
  const fields = strings.fields;
  const { resolving, resolveCustomerAddress } = useCustomerDeliveryLocation();
  const REQUIRED = t('common.form.requiredIndicator');

  // short-circuit render only after hooks are declared
  const defaultNewCustomer = useMemo(() => ({
    owner_name: '',
    company_name: '',
    gst_number: '',
    contact_person: '',
    customer_type: fields?.customer?.customer_type?.placeholder,
    address_line1: '',
    state: '',
    city: '',
    country: t('locations.defaults.country'),
    zip_code: '',
    email: '',
    phone_number_1: '',
    tally_name: '',
    credit_period: 0,
    reference_party_name: '',
  }), [fields?.customer?.customer_type?.placeholder, t]);

  // mode: 'existing' or 'new'
  // When `inquiry` is not provided (adding a new inquiry) default to 'existing'
  const initialMode = 'existing';
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState(0); // for new customer: 0 = customer info, 1 = warehouses, 2 = order info
  const [newCustomerWarehouses, setNewCustomerWarehouses] = useState([]);
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);

  // State for fetching inquiry data
  const [currentInquiry, setCurrentInquiry] = useState(inquiry);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  // Guard ref to avoid duplicate fetches for the same inquiry id
  const lastFetchedIdRef = useRef(null);

  // Dropdown caches
  if (!window.inquiryFormCache) {
    window.inquiryFormCache = { customers: null, locations: null, products: null, loaded: false, loading: false };
  }
  const cache = window.inquiryFormCache;

  // Function to clear cache if needed (useful for development or when data changes)
  // eslint-disable-next-line no-unused-vars
  const clearCache = useCallback(() => {
    window.inquiryFormCache = { customers: null, locations: null, products: null, loaded: false, loading: false };
    setCustomers([]);
    setLocations([]);
    setProducts([]);
  }, []);

  const [customers, setCustomers] = useState([]);
  const [selectedCustomerDetail, setSelectedCustomerDetail] = useState(null);
  // Open when the customer's address is missing a part (usually city) that must be
  // collected before it can become a delivery location.
  const [showAddressPrompt, setShowAddressPrompt] = useState(false);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const customerSearchRef = useRef(null);
  const customerActiveTermRef = useRef('');
  const [productSearchLoading, setProductSearchLoading] = useState(new Set());
  const productSearchTimers = useRef(new Map());
  const productActiveTermRef = useRef('');
  const [supplierSearchLoading, setSupplierSearchLoading] = useState(false);
  const supplierSearchRef = useRef(null);

  const [suppliers, setSuppliers] = useState([]);
  const [locationSearchLoading, setLocationSearchLoading] = useState(false);
  const locationSearchRef = useRef(null);
  const [warehouseSearchLoading, setWarehouseSearchLoading] = useState(false);
  const warehouseSearchRef = useRef(null);



  // Form state for order fields (shared)
  const [order, setOrder] = useState({
    customer_id: currentInquiry?.customer_id ?? '',
    location_id: currentInquiry?.location_id ?? '',
    freight: currentInquiry?.freight ?? '',
    freight_type: currentInquiry?.freight_type ?? '',
    freight_pricing_type: currentInquiry?.freight_pricing_type ?? 'fixed',
    price_list_date: currentInquiry?.price_list_date ?? '',
    expected_delivery_date: currentInquiry?.expected_delivery_date ?? getTomorrowDate(),
    include_gst: currentInquiry?.include_gst ?? currentInquiry?.gst_included ?? false,
    source_type: '',
    selected_warehouse_id: '',
    selected_supplier_id: '',
    loading_cost_per_kg: '',
    reference_party_name: currentInquiry?.reference_party_name ?? '',
    remark: currentInquiry?.remark ?? '',
    items: Array.isArray(currentInquiry?.items) && currentInquiry.items.length > 0 ?
      currentInquiry.items.map(i => ({
        product_size_id: i.product_size_id || '',
        units: normalizeUnit(i.units) || 'kg',
        length: i.length || '',
        quantity: i.quantity || '',
        quantity_in_pieces: i.quantity_in_pieces || i.pieces || i.quantity_per_pieces || '',
        cut_pieces: i.cut_pieces ?? '',
        coil_tracked: !!(i.coil_tracked),
        price: (i.price === null || i.price === undefined) ? '' : String(i.price),   // a ₹0 line stays ₹0, not "unpriced"
        remark: i.remark || ''
      })) : [emptyItem()],
  });

  const formOrderRef = useRef(order);
  useEffect(() => {
    formOrderRef.current = order;
  }, [order]);


  const [newCustomer, setNewCustomer] = useState(currentInquiry?.customer || defaultNewCustomer);
  const [errors, setErrors] = useState({});

  const handleCustomerSearch = useCallback((term) => {
    clearTimeout(customerSearchRef.current);
    customerActiveTermRef.current = term;
    if (!term.trim()) {
      setCustomerSearchLoading(false);
      setCustomers((prev) => {
        const base = cache.customers || [];
        const selectedId = String(formOrderRef.current?.customer_id || '');
        if (selectedId && !base.some((c) => String(c.id) === String(selectedId))) {
          const selected = prev.find((c) => String(c.id) === String(selectedId));
          return selected ? [selected, ...base] : base;
        }
        return base;
      });
      return;
    }
    setCustomerSearchLoading(true);
    customerSearchRef.current = setTimeout(async () => {
      try {
        const response = await customersService.getCustomers({ search: term, limit: 20, is_active: true });
        if (customerActiveTermRef.current !== term) return;
        const list = Array.isArray(response?.data) ? response.data : [];
        setCustomers((prev) => {
          // Always keep the currently selected customer in the list
          const selectedId = String(formOrderRef.current?.customer_id || '');
          if (selectedId) {
            const selected = prev.find((c) => String(c.id) === String(selectedId));
            if (selected && !list.some((c) => String(c.id) === String(selectedId))) {
              return [selected, ...list];
            }
          }
          return list;
        });
      } catch (e) {
        // ignore search errors silently
      } finally {
        if (customerActiveTermRef.current === term) setCustomerSearchLoading(false);
      }
    }, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache]);

  const handleProductSearch = useCallback((idx, term) => {
    clearTimeout(productSearchTimers.current.get(idx));
    productActiveTermRef.current = term;
    if (!term.trim()) {
      setProductSearchLoading(prev => { const next = new Set(prev); next.delete(idx); return next; });
      setProducts(cache.products || []);
      return;
    }
    productSearchTimers.current.set(idx, setTimeout(async () => {
      setProductSearchLoading(prev => new Set([...prev, idx]));
      try {
        const response = await productSizesService.getAllProductSizes({ search: term, limit: 20 }, { force: true });
        if (productActiveTermRef.current !== term) return;
        const raw = response?.data;
        const data = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []);
        if (cache.products) {
          const existingIds = new Set(cache.products.map(p => String(p.id)));
          const newProducts = data.filter(p => !existingIds.has(String(p.id)));
          if (newProducts.length > 0) {
            cache.products = [...cache.products, ...newProducts];
          }
        }
        setProducts(data);
      } catch (e) {
        // ignore search errors silently
      } finally {
        if (productActiveTermRef.current === term) setProductSearchLoading(prev => { const next = new Set(prev); next.delete(idx); return next; });
      }
    }, 400));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache]);

  const handleSupplierSearch = useCallback((term) => {
    clearTimeout(supplierSearchRef.current);
    if (!term.trim()) {
      setSupplierSearchLoading(false);
      setSuppliers((prev) => {
        const base = cache.suppliers || prev;
        const selectedId = String(formOrderRef.current?.selected_supplier_id || '');
        if (selectedId && !base.some((c) => String(c.id) === String(selectedId))) {
          const selected = prev.find((c) => String(c.id) === String(selectedId));
          return selected ? [selected, ...base] : base;
        }
        return base;
      });
      return;
    }
    setSupplierSearchLoading(true);
    supplierSearchRef.current = setTimeout(async () => {
      try {
        const response = await customersService.getCustomers({ search: term, customer_type: 'Supplier', limit: 20, is_active: true });
        const all = Array.isArray(response?.data) ? response.data : [];
        const list = all.filter(c => String(c.customer_type ?? c.type ?? c.customerType ?? '').toLowerCase() === 'supplier');
        setSuppliers((prev) => {
          const selectedId = String(formOrderRef.current?.selected_supplier_id || '');
          if (selectedId) {
            const selected = prev.find((c) => String(c.id) === String(selectedId));
            if (selected && !list.some((c) => String(c.id) === String(selectedId))) {
              return [selected, ...list];
            }
          }
          return list;
        });
      } catch (e) {
        // ignore search errors silently
      } finally {
        setSupplierSearchLoading(false);
      }
    }, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache]);

  const handleLocationSearch = useCallback((term) => {
    if (locationSearchRef.current) clearTimeout(locationSearchRef.current);
    if (!term.trim()) {
      setLocationSearchLoading(false);
      setLocations((prev) => {
        const base = cache.locations || prev;
        const selectedId = String(formOrderRef.current?.location_id || '');
        if (selectedId && !base.some((c) => String(c.id) === String(selectedId))) {
          const selected = prev.find((c) => String(c.id) === String(selectedId));
          return selected ? [selected, ...base] : base;
        }
        return base;
      });
      return;
    }
    setLocationSearchLoading(true);
    locationSearchRef.current = setTimeout(async () => {
      try {
        const response = await locationsService.getLocations({ search: term, limit: 20 });
        const list = Array.isArray(response?.data) ? response.data : [];
        setLocations((prev) => {
          const selectedId = String(formOrderRef.current?.location_id || '');
          if (selectedId) {
            const selected = prev.find((c) => String(c.id) === String(selectedId));
            if (selected && !list.some((c) => String(c.id) === String(selectedId))) {
              return [selected, ...list];
            }
          }
          return list;
        });
      } catch (e) {
      } finally {
        setLocationSearchLoading(false);
      }
    }, 400);
  }, [cache]);

  const handleWarehouseSearch = useCallback((term) => {
    if (warehouseSearchRef.current) clearTimeout(warehouseSearchRef.current);
    if (!term.trim()) {
      setWarehouseSearchLoading(false);
      setWarehouses((prev) => {
        const base = cache.warehouses || prev;
        const selectedId = String(formOrderRef.current?.selected_warehouse_id || '');
        if (selectedId && !base.some((w) => String(w.id) === String(selectedId))) {
          const selected = prev.find((w) => String(w.id) === String(selectedId));
          return selected ? [selected, ...base] : base;
        }
        return base;
      });
      return;
    }
    setWarehouseSearchLoading(true);
    warehouseSearchRef.current = setTimeout(async () => {
      try {
        const response = await warehouseService.getAllWarehouses({ search: term, limit: 20 });
        const list = Array.isArray(response?.data) ? response.data : [];
        setWarehouses((prev) => {
          const selectedId = String(formOrderRef.current?.selected_warehouse_id || '');
          if (selectedId) {
            const selected = prev.find((w) => String(w.id) === String(selectedId));
            if (selected && !list.some((w) => String(w.id) === String(selectedId))) {
              return [selected, ...list];
            }
          }
          return list;
        });
      } catch (e) {
      } finally {
        setWarehouseSearchLoading(false);
      }
    }, 400);
  }, [cache]);

  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [locationAddInitialValue, setLocationAddInitialValue] = useState('');

  const handleAddLocationSuccess = (newLocation) => {
    setLocations(prev => {
        const hasIt = prev.some(l => l.id === newLocation.id);
        if (hasIt) return prev;
        return [...prev, newLocation];
    });
    if (cache.locations) {
        if (!cache.locations.some(l => l.id === newLocation.id)) {
            cache.locations = [...cache.locations, newLocation];
        }
    }
    setOrder(prev => ({ ...prev, location_id: newLocation.id }));
  };

  // The customers LIST payload omits address lines, so the selected customer's detail is
  // fetched to build the "use customer's address" option. Same pattern as OrdersForm.
  useEffect(() => {
    let cancelled = false;
    if (mode !== 'existing' || !order.customer_id) {
      setSelectedCustomerDetail(null);
      return () => { cancelled = true; };
    }
    (async () => {
      const res = await customersService.getCustomerById(order.customer_id);
      if (!cancelled && res?.success) setSelectedCustomerDetail(res.data);
    })();
    return () => { cancelled = true; };
  }, [mode, order.customer_id]);

  const customerAddressDraft = useMemo(() => {
    if (mode === 'new') return buildCustomerAddressDraft(newCustomer);
    if (!order.customer_id) return null;
    const listRow = customers.find(c => String(c.id) === String(order.customer_id));
    return buildCustomerAddressDraft({ ...(listRow || {}), ...(selectedCustomerDetail || {}) });
  }, [mode, order.customer_id, customers, selectedCustomerDetail, newCustomer]);

  const deliveryLocationOptions = useMemo(() => {
    const isNew = mode === 'new';
    const customer = isNew
      ? null
      : customers.find(c => String(c.id) === String(order.customer_id));
    return buildDeliveryLocationOptions({
      warehouses: isNew
        ? (Array.isArray(newCustomerWarehouses) ? newCustomerWarehouses : [])
        : (order.customer_id && Array.isArray(customer?.warehouses) ? customer.warehouses : []),
      warehouseShape: isNew ? 'offline' : 'saved',
      locations,
      selectedId: order.location_id,
      addressDraft: customerAddressDraft,
      labels: {
        warehouse: '(Warehouse)',
        default: '[Default]',
        useCustomerAddress: t('customerWarehouses.labels.useCustomerAddress'),
        needsDetails: t('customerWarehouses.labels.needsDetails'),
      },
      // Inquiry keeps its own last-resort label for a location with no usable name.
      locationLabel: (l) => l.delivery_location || l.city || l.name || `Location ${l.id}`,
    });
  }, [mode, order.customer_id, order.location_id, customers, locations, newCustomerWarehouses, customerAddressDraft, t]);

  const defaultSiteName = useCallback(() => {
    if (mode === 'new') return newCustomer?.company_name || newCustomer?.owner_name || '';
    const listRow = customers.find(c => String(c.id) === String(order.customer_id));
    return listRow?.tally_name || listRow?.company_name || listRow?.owner_name || '';
  }, [mode, newCustomer, customers, order.customer_id]);

  /** Save a completed draft as a delivery site and select it. */
  const commitCustomerAddress = useCallback(async (draft, siteName) => {
    const isNew = mode === 'new';
    const result = await resolveCustomerAddress({
      mode: isNew ? 'new' : 'existing',
      customerId: order.customer_id,
      draft,
      warehouseName: siteName || defaultSiteName(),
    });

    if (!result.success) {
      setErrors(prev => ({ ...prev, location_id: result.error }));
      return;
    }

    handleAddLocationSuccess(result.location);

    if (isNew) {
      setNewCustomerWarehouses(prev => [...prev, result.warehouse]);
    } else if (result.warehouse) {
      setCustomers(prev => prev.map(c => (
        String(c.id) === String(order.customer_id)
          ? { ...c, warehouses: [...(c.warehouses || []),
              // Carry the nested location: the option-builder keys the
              // "already covered" check off it, so without it the address
              // option would linger after being saved.
              { ...result.warehouse, location: result.warehouse?.location || result.location }] }
          : c
      )));
    }
    setErrors(prev => ({ ...prev, location_id: null }));
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, order.customer_id, resolveCustomerAddress, defaultSiteName]);

  /**
   * Picking "use customer's address". Complete addresses save straight through; an
   * incomplete one (no city, which is almost every imported customer) opens the
   * warehouse form prefilled so the missing part is typed once and then remembered.
   */
  const handleCustomerAddressPick = useCallback(() => {
    if (!customerAddressDraft?.offerable || resolving) return;
    if (customerAddressDraft.materializable) {
      commitCustomerAddress(customerAddressDraft, defaultSiteName());
    } else {
      setShowAddressPrompt(true);
    }
  }, [customerAddressDraft, resolving, commitCustomerAddress, defaultSiteName]);

  // Memoized: CustomerWarehouseForm re-seeds its fields whenever this prop's IDENTITY
  // changes, so a fresh literal each render would wipe what the user is typing.
  const addressPromptInitialData = useMemo(() => ({
    name: defaultSiteName(),
    country: customerAddressDraft?.country || 'India',
    state: customerAddressDraft?.state || '',
    city: customerAddressDraft?.city || '',
    address: customerAddressDraft?.delivery_location || '',
  }), [defaultSiteName, customerAddressDraft]);

  // Fetch inquiry data when inquiryId is provided but inquiry data is not
  const fetchInquiryData = useCallback(async (id) => {
    if (!id) return null;

    // If we've already initiated a fetch for this id, skip to avoid duplicate API calls
    if (lastFetchedIdRef.current === id) return null;

    lastFetchedIdRef.current = id;
    setInquiryLoading(true);
    try {
      const result = await inquiriesService.getInquiryById(id, { force: true });
      if (result && result.success && result.data) {
        setCurrentInquiry(result.data);
        return result.data;
      }
      console.error('Failed to fetch inquiry:', result && result.error);
      return null;
    } catch (error) {
      console.error('Error fetching inquiry:', error);
      return null;
    } finally {
      setInquiryLoading(false);
    }
  }, []);



  // Reset lastFetchedIdRef when modal is closed so subsequent opens can fetch new ids
  useEffect(() => {
    if (!isOpen) {
      lastFetchedIdRef.current = null;
      setCurrentInquiry(inquiry || null);
    }
  }, [isOpen, inquiry]);

  useEffect(() => {
    // initialize when open or inquiry changes
    // When editing an existing inquiry, do not allow switching to 'new' customer mode.
    const activeInquiry = currentInquiry || inquiry;

    // If we have an inquiryId but no inquiry data, fetch it
    if (isOpen && inquiryId && !inquiry && !currentInquiry) {
      fetchInquiryData(inquiryId).then((fetchedInquiry) => {
        if (fetchedInquiry) {
          // Update form state with fetched data
          setOrder({
            customer_id: fetchedInquiry?.customer_id ?? '',
            location_id: fetchedInquiry?.location_id ?? '',
            freight: fetchedInquiry?.freight ?? '',
            freight_type: fetchedInquiry?.freight_type ?? '',
            freight_pricing_type: fetchedInquiry?.freight_pricing_type ?? 'fixed',
            price_list_date: fetchedInquiry?.price_list_date ?? '',
            expected_delivery_date: fetchedInquiry?.expected_delivery_date ?? getTomorrowDate(),
            include_gst: fetchedInquiry?.include_gst ?? fetchedInquiry?.gst_included ?? false,
            remark: fetchedInquiry?.remark ?? '',
            items: Array.isArray(fetchedInquiry?.items) && fetchedInquiry.items.length > 0 ?
              fetchedInquiry.items.map(i => ({
                product_size_id: i.product_size_id || '',
                product_name: i.product_name || i.product?.name || '',
                size_name: i.size_name || i.alias_name || '',
                units: normalizeUnit(i.units) || 'kg',
                length: i.length || '',
                quantity: i.quantity || '',
                quantity_in_pieces: i.quantity_in_pieces || i.pieces || i.quantity_per_pieces || '',
                cut_pieces: i.cut_pieces ?? '',
                coil_tracked: !!(i.coil_tracked),
                price: (i.price === null || i.price === undefined) ? '' : String(i.price),   // a ₹0 line stays ₹0, not "unpriced"
                remark: i.remark || ''
              })) : [emptyItem()],
            // Conversion fields defaults
            source_type: '',
            loading_cost_per_kg: '',
            selected_warehouse_id: '',
            selected_supplier_id: '',
          });
          setNewCustomer(fetchedInquiry?.customer || defaultNewCustomer);
        }
      });
      return; // Don't continue with initialization if we're fetching
    }

    setMode(activeInquiry ? 'existing' : initialMode);
    setStep(0);
    setOrder({
      customer_id: activeInquiry?.customer_id ?? '',
      location_id: activeInquiry?.location_id ?? '',
      freight: activeInquiry?.freight ?? '',
      freight_type: activeInquiry?.freight_type ?? '',
      freight_pricing_type: activeInquiry?.freight_pricing_type ?? 'fixed',
      price_list_date: activeInquiry?.price_list_date ?? '',
      expected_delivery_date: activeInquiry?.expected_delivery_date ?? getTomorrowDate(),
      include_gst: activeInquiry?.include_gst ?? activeInquiry?.gst_included ?? false,
      reference_party_name: activeInquiry?.reference_party_name ?? '',
      remark: activeInquiry?.remark ?? '',
      items: Array.isArray(activeInquiry?.items) && activeInquiry.items.length > 0 ?
        activeInquiry.items.map(i => ({
          product_size_id: i.product_size_id || '',
          product_name: i.product_name || i.product?.name || '',
          size_name: i.size_name || i.alias_name || '',
          units: normalizeUnit(i.units) || 'kg',
          length: i.length || '',
          quantity: i.quantity || '',
          quantity_in_pieces: i.quantity_in_pieces || i.pieces || i.quantity_per_pieces || '',
          cut_pieces: i.cut_pieces ?? '',
          coil_tracked: !!(i.coil_tracked),
          price: (i.price === null || i.price === undefined) ? '' : String(i.price),   // a ₹0 line stays ₹0, not "unpriced"
          remark: i.remark || ''
        })) : [emptyItem()],
      // Conversion fields defaults
      loading_cost_per_kg: '',
      selected_warehouse_id: '',
      selected_supplier_id: '',
    });
    setNewCustomer(activeInquiry?.customer || defaultNewCustomer);
    setErrors({});
  }, [isOpen, inquiry, currentInquiry, inquiryId, defaultNewCustomer, initialMode, fetchInquiryData]);




  const loadDropdowns = useCallback(async () => {
    // If data is already cached and current state is empty, load from cache
    if (cache.loaded) {
      if (suppliers.length === 0 && cache.suppliers) setSuppliers(cache.suppliers);
      if (locations.length === 0 && cache.locations) setLocations(cache.locations);
      if (warehouses.length === 0 && cache.warehouses) setWarehouses(cache.warehouses);
      // SKUs are never served from the window cache: a price edited in Product
      // Sizes must show on the very next form open (P1-4).
      productSizesService.getAllProductSizes({}, { force: true })
        .then((resp) => {
          const raw = resp?.data;
          const fresh = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []);
          // Keep SKUs already injected for the inquiry's own lines (they may sit
          // outside the first page) so an open line never loses its selected product.
          const seen = new Set(fresh.map((p) => String(p.id)));
          const merged = [...fresh, ...(cache.products || []).filter((p) => !seen.has(String(p.id)))];
          cache.products = merged;
          // A search typed meanwhile owns the dropdown; the cache is enough for it.
          if (!productActiveTermRef.current) setProducts(merged);
        })
        .catch(() => { /* keep whatever is already shown */ });
      return;
    }

    // Prevent multiple simultaneous API calls
    if (cache.loading) return;

    cache.loading = true;
    setDropdownsLoading(true);

    try {

      // Call all APIs in parallel
      const [customersResponse, locationsResponse, productSizesResponse, warehousesResponse] = await Promise.all([
        customersService.getCustomers({ is_active: true }).catch((error) => {
          console.error('Error loading customers:', error);
          return { data: [] };
        }),
        locationsService.getLocations().catch((error) => {
          console.error('Error loading locations:', error);
          return { data: [] };
        }),
        // Fresh SKUs every time — a cached list hid price edits (P1-4).
        productSizesService.getAllProductSizes({}, { force: true }).catch((error) => {
          console.error('Error loading product sizes:', error);
          return { data: [] };
        }),
        warehouseService.getAllWarehouses().catch((error) => {
          console.error('Error loading warehouses:', error);
          return { data: [] };
        }),
      ]);

      // Extract data arrays, handling different response structures
      const customersData = Array.isArray(customersResponse?.data) ? customersResponse.data :
        Array.isArray(customersResponse) ? customersResponse : [];
      const locationsData = Array.isArray(locationsResponse?.data) ? locationsResponse.data :
        Array.isArray(locationsResponse) ? locationsResponse : [];
      const productsRaw = productSizesResponse?.data;
      const productsData = Array.isArray(productsRaw) ? productsRaw :
        Array.isArray(productsRaw?.items) ? productsRaw.items :
        Array.isArray(productSizesResponse) ? productSizesResponse : [];
      const warehousesData = Array.isArray(warehousesResponse?.data) ? warehousesResponse.data :
        Array.isArray(warehousesResponse) ? warehousesResponse : [];

      const suppliersData = customersData.filter(c => String(c.customer_type ?? c.type ?? c.customerType ?? '').toLowerCase() === 'supplier');

      // Update state
      setCustomers(customersData);
      setSuppliers(suppliersData);
      setLocations(locationsData);
      setProducts(productsData);

      // Cache the data
      cache.customers = customersData;
      cache.suppliers = suppliersData;
      cache.locations = locationsData;
      cache.products = productsData;
      cache.warehouses = warehousesData;
      cache.loaded = true;

    } catch (error) {
      console.error('Error loading dropdown data:', error);
      // Set empty arrays on error to prevent undefined issues
      setCustomers([]);
      setLocations([]);
      setProducts([]);
      setWarehouses([]);
    } finally {
      cache.loading = false;
      setDropdownsLoading(false);
    }
  }, [cache, suppliers.length, locations.length, warehouses.length]);

  useEffect(() => {
    if (isOpen) loadDropdowns();
  }, [isOpen, loadDropdowns]);

  // If in edit mode, inject nested objects from `inquiry` into local lists and cache.
  // IMPORTANT: do NOT fetch single resources by id here — avoid extra API calls when the
  // parent `inquiry` payload already contains the nested objects. This prevents
  // GET /customers/:id (and similar) from being called when opening the edit form.
  useEffect(() => {
    const activeInquiry = currentInquiry || inquiry;
    if (!activeInquiry) return;

    // Helper to prepend into cache and state only if not already present in cache
    const prependIfMissing = (listName, item) => {
      if (!item || !item.id) return;
      const cached = cache[listName] || [];
      if (cached.some((c) => String(c.id) === String(item.id))) return;
      // update cache and state
      cache[listName] = [item, ...cached];
      if (listName === 'customers') setCustomers(prev => [item, ...prev]);
      if (listName === 'locations') setLocations(prev => [item, ...prev]);
      if (listName === 'products') setProducts(prev => [item, ...prev]);
    };

    // Customer: only inject if activeInquiry.customer object exists (do NOT fetch by id)
    if (activeInquiry.customer && typeof activeInquiry.customer === 'object') {
      const cust = { id: activeInquiry.customer_id, ...(activeInquiry.customer) };
      prependIfMissing('customers', cust);
    } else if (activeInquiry.customer_id && (activeInquiry.customer_name || activeInquiry.customer_company)) {
      const cust = { 
        id: activeInquiry.customer_id, 
        owner_name: activeInquiry.customer_name || undefined, 
        company_name: activeInquiry.customer_company || undefined 
      };
      prependIfMissing('customers', cust);
    }

    // Location: prefer activeInquiry.location object or top-level city/delivery_location values
    if (activeInquiry.location && typeof activeInquiry.location === 'object') {
      const loc = { id: activeInquiry.location_id, ...(activeInquiry.location) };
      prependIfMissing('locations', loc);
    } else if (activeInquiry.location_id && (activeInquiry.city || activeInquiry.delivery_location)) {
      const loc = { id: activeInquiry.location_id, city: activeInquiry.city || undefined, delivery_location: activeInquiry.delivery_location || undefined };
      prependIfMissing('locations', loc);
    }

    // Product size: inject from top-level product_size object if present
    if (activeInquiry.product_size && typeof activeInquiry.product_size === 'object') {
      const ps = { id: activeInquiry.product_size_id, ...(activeInquiry.product_size) };
      prependIfMissing('products', ps);
    }
    // Inject products from items array (inquiry response includes product info per item)
    if (Array.isArray(activeInquiry.items)) {
      activeInquiry.items.forEach(item => {
        if (item.product_size_id) {
          prependIfMissing('products', {
            id: item.product_size_id,
            alias_name: item.product_alias || item.alias_name,
            product_name: item.product_name,
            category_name: item.category_name,
          });
        }
      });
    }
    // Intentionally depend only on `inquiry` and `currentInquiry` so this runs once when opening/editing a record.
  }, [inquiry, currentInquiry, cache]);

  // Auto-calculate pieces from quantity once both products and inquiry items are loaded
  useEffect(() => {
    if (!isOpen || products.length === 0) return;

    setOrder(prev => {
      const items = Array.isArray(prev.items) ? prev.items : [];
      let changed = false;
      const newItems = items.map(item => {
        // Skip if pieces already set, or missing quantity/product
        if (item.quantity_in_pieces !== '' && item.quantity_in_pieces !== null && item.quantity_in_pieces !== undefined) return item;
        if (!item.quantity || !item.product_size_id) return item;
        const product = products.find(p => String(p.id) === String(item.product_size_id));
        if (!product || (!product.product_weight && !product.standard_length)) return item;

        const pWeight = parseFloat(product.product_weight) || 0;
        const sLength = parseFloat(product.standard_length) || 1;
        const rowLength = parseFloat(item.length) || sLength;
        const weightPerPiece = (pWeight / sLength) * rowLength;
        const qty = parseFloat(item.quantity) || 0;
        let pieces = '';

        if (isWeightUnit(item.units) && weightPerPiece > 0) {
          pieces = sanitizeNumber((qty / weightPerPiece).toFixed(3));
        } else if (isLengthUnit(item.units) && rowLength > 0) {
          pieces = sanitizeNumber((qty / rowLength).toFixed(3));
        }

        if (pieces) { changed = true; return { ...item, quantity_in_pieces: pieces }; }
        return item;
      });
      if (changed) return { ...prev, items: newItems };
      return prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, isOpen, order.items.length]);

  const handleOrderChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = value;

    // "Use customer's address" is an action, not a location — resolve it to a real id
    // instead of letting the sentinel reach state, where submit's
    // `Number(location_id) || 0` would turn it into a silent 0.
    if (name === 'location_id' && isSyntheticLocationValue(v)) {
      handleCustomerAddressPick();
      return;
    }

    if (type === 'checkbox') v = checked;
    if (['freight', 'loading_cost_per_kg'].includes(name)) v = sanitizeNumber(v);
    setOrder(prev => {
      const next = { ...prev, [name]: v };
      if (name === 'customer_id' && v) {
        const selectedCustomer = customers.find(c => String(c.id) === String(v));
        if (selectedCustomer) {
          if (Array.isArray(selectedCustomer.warehouses)) {
            const defaultWh = selectedCustomer.warehouses.find(w => w.is_default);
            if (defaultWh && defaultWh.location_id) {
              next.location_id = defaultWh.location_id;
            }
          }
          if (selectedCustomer.reference_party_name) {
            next.reference_party_name = selectedCustomer.reference_party_name;
          }
        }
      }
      return next;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  // A line is coil-tracked when BOTH the global coil-tracking switch is on AND
  // the selected SKU carries coil_tracked — the same two-level gate the order
  // form uses. Inquiries don't pick coils, so this only drives the Cut Pieces field.
  const isCoilTrackedLine = useCallback((item) => {
    if (!coilTrackingEnabled || !item?.product_size_id) return false;
    // Trust the line's own flag first (set from the response on edit, or stamped
    // on product change): the product catalogue is paginated, so a coil SKU past
    // the first page is not in `products` — relying on that lookup alone would
    // hide the field and drop the value on a reopened inquiry.
    if (item.coil_tracked) return true;
    const product = (products || []).find(p => String(p.id) === String(item.product_size_id));
    return Boolean(product?.coil_tracked);
  }, [coilTrackingEnabled, products]);

  const handleItemChange = (index, field, value) => {
    const newVal = ['quantity', 'price', 'length', 'quantity_in_pieces', 'cut_pieces'].includes(field) ? sanitizeNumber(value) : value;

    setOrder(prev => {
      const items = Array.isArray(prev.items) ? [...prev.items] : [];
      const updatedItem = {
        ...items[index],
        [field]: newVal
      };

      // Calculate quantity_in_pieces or Quantity based on each other and product specifications
      const product = products.find(p => String(p.id) === String(updatedItem.product_size_id));
      if (field === 'product_size_id') {
        // Auto-fill the line price from the selected SKU's master price
        // (migration 061). Unpriced SKU -> blank line; editable afterwards.
        const skuPrice = product ? (product.base_price ?? product.price) : null;
        updatedItem.price = (skuPrice !== null && skuPrice !== undefined && skuPrice !== '')
          ? String(skuPrice)
          : '';
      }
      if (product && field === 'product_size_id') {
        // Stamp the coil flag onto the line so the coil-only Cut Pieces field
        // survives even when the SKU later drops out of the paginated `products`.
        updatedItem.coil_tracked = !!product.coil_tracked;
      }
      if (product) {
        const pWeight = parseFloat(product.product_weight) || 0;
        const sLength = parseFloat(product.standard_length) || 1;
        const rowLength = parseFloat(updatedItem.length) || sLength;
        const weightPerPiece = (pWeight / sLength) * rowLength;

        // Capture product names for validation error messages
        updatedItem.product_name = product.product_name || product.name || updatedItem.product_name || '';
        updatedItem.size_name = product.alias_name || product.size_name || updatedItem.size_name || '';

        if (field === 'quantity') {
          const qty = parseFloat(sanitizeNumber(value)) || 0;
          if (isWeightUnit(updatedItem.units) && weightPerPiece > 0) {
            updatedItem.quantity_in_pieces = String(Math.round(qty / weightPerPiece));
          } else if (isLengthUnit(updatedItem.units) && rowLength > 0) {
            updatedItem.quantity_in_pieces = String(Math.round(qty / rowLength));
          }
        } else if (field === 'quantity_in_pieces') {
          const pcs = parseFloat(sanitizeNumber(value)) || 0;
          if (isWeightUnit(updatedItem.units) && weightPerPiece > 0) {
            updatedItem.quantity = sanitizeNumber((pcs * weightPerPiece).toFixed(3));
          } else if (isLengthUnit(updatedItem.units) && rowLength > 0) {
            updatedItem.quantity = sanitizeNumber((pcs * rowLength).toFixed(3));
          }
        } else if (['units', 'product_size_id', 'length'].includes(field)) {
          // If units, product or length changes, recalculate quantity from quantity_in_pieces to maintain physical count
          const pcs = sanitizeNumber(updatedItem.quantity_in_pieces);
          if (pcs && !isNaN(pcs) && pcs !== '') {
            if (isWeightUnit(updatedItem.units) && weightPerPiece > 0) {
              updatedItem.quantity = sanitizeNumber((pcs * weightPerPiece).toFixed(3));
            } else if (isLengthUnit(updatedItem.units) && rowLength > 0) {
              updatedItem.quantity = sanitizeNumber((pcs * rowLength).toFixed(3));
            }
          }
        }
      }

      items[index] = updatedItem;

      // Auto-add next line if this is the last item and all mandatory fields are filled (Add mode only)
      const isAddMode = !inquiry && !currentInquiry;
      if (isAddMode && index === items.length - 1) {
        // price intentionally omitted — optional now (migration 061).
        const isFilled = updatedItem.product_size_id &&
                         updatedItem.quantity &&
                         updatedItem.units &&
                         updatedItem.length &&
                         parseFloat(updatedItem.quantity) > 0;
        
        if (isFilled) {
          items.push(emptyItem());
        }
      }

      return { ...prev, items };
    });

    // Clear item-level validation error for this field when user fills valid data
    const errorKey = `items.${index}.${field}`;
    if (field === 'product_size_id') {
      if (newVal) setErrors(prev => ({ ...prev, [errorKey]: null }));
    } else if (['quantity', 'price', 'quantity_in_pieces'].includes(field)) {
      const num = Number(newVal);
      if (newVal !== '' && !Number.isNaN(num)) {
        if (field === 'quantity' || field === 'quantity_in_pieces') {
          if (num > 0) setErrors(prev => ({ ...prev, [errorKey]: null }));
        } else {
          // price can be zero or positive
          if (num >= 0) setErrors(prev => ({ ...prev, [errorKey]: null }));
        }
      }
    } else {
      if (newVal !== '' && newVal !== null && typeof newVal !== 'undefined') setErrors(prev => ({ ...prev, [errorKey]: null }));
    }
  };

  const addItem = () => {
    const items = Array.isArray(order.items) ? [...order.items] : [];
    const lastIndex = items.length - 1;

    // Use centralized validator for order items from validateUtils
    const itemErrors = validateUtils.validateInquiryItems(items);

    // If there are any item errors, check if they concern the last item or overall items presence
    if (itemErrors && Object.keys(itemErrors).length > 0) {
      // If error is for the collection or for the last item, prevent adding
      const relevant = Object.keys(itemErrors).some(k => k === 'items' || k.startsWith(`items.${lastIndex}`));
      if (relevant) {
        setErrors(prev => ({ ...prev, ...itemErrors }));
        return; // don't add new item until previous is complete
      }
    }

    setOrder(prev => ({ ...prev, items: [...(prev.items || []), emptyItem()] }));
  };
  const removeItem = (idx) => {
    setOrder(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
    // Remove any errors tied to this item index
    setErrors(prev => {
      const next = {};
      Object.keys(prev || {}).forEach(k => {
        if (!k.startsWith(`items.${idx}`)) next[k] = prev[k];
      });
      return next;
    });
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'phone_number_1') {
      // Limit to 10 digits and allow only numeric characters
      newValue = value.replace(/\D/g, '').slice(0, 10);
    }

    setNewCustomer(prev => ({ ...prev, [name]: newValue }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validateStep = () => {
    let v = {};

    // Tag each line with whether it's coil-tracked so the shared item validator
    // (which has no product data) can apply the coil-only cut-pieces rule.
    // Non-mutating copy — never touch the form state here.
    const orderForValidate = {
      ...(order || {}),
      items: ((order && order.items) || []).map(it => ({ ...it, __coilTracked: isCoilTrackedLine(it) })),
    };

    try {
      if (mode === 'existing') {
        v = validateUtils.validateExistingCustomerInquiry(orderForValidate);
      } else if (mode === 'new') {
        if (step === 0 || step === 1) {
          v = validateUtils.validateNewCustomerData(newCustomer || {});
        } else {
          v = validateUtils.validateNewCustomerInquiry(orderForValidate, newCustomer || {});
        }
      }
    } catch (err) {
      console.error('Error running centralized validators:', err);
      v = v || {};
    }

    // Price list date is mandatory on internally-created inquiries (the order
    // details step: existing mode, or new mode step 2).
    if ((mode === 'existing' || (mode === 'new' && step === 2)) && !order.price_list_date) {
      v.price_list_date = strings.validation?.required || 'Price list date is required';
    }

    setErrors(v);

    // If converting, perform additional manual validation for conversion fields
    if (isConverting && (mode === 'existing' || (mode === 'new' && step === 2))) {
      if (!order.source_type) {
        v.source_type = strings.validation?.required || 'Required';
      } else {
        if (order.source_type === 'warehouse' && !order.selected_warehouse_id) {
          v.selected_warehouse_id = strings.validation?.required || 'Required';
        }
        if (order.source_type === 'supplier' && !order.selected_supplier_id) {
          v.selected_supplier_id = strings.validation?.required || 'Required';
        }
      }
      setErrors(v);
    }

    return Object.keys(v).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep()) return;
    if (step === 0 && mode === 'new') {
      setStep(1); // Go to Warehouse step
    } else if (step === 1 && mode === 'new') {
      // Select the warehouse's REAL location id. This used to set the string
      // 'new_wh_0', and submit does `Number(location_id) || 0` — so choosing an
      // offline warehouse silently posted location_id: 0. The offline warehouses
      // already carry a real location_id by the time they land here.
      if (newCustomerWarehouses.length > 0 && !order.location_id) {
        const first = newCustomerWarehouses.find(w => w.is_default) || newCustomerWarehouses[0];
        if (first?.location_id) {
          setOrder(prev => ({ ...prev, location_id: first.location_id }));
        }
      }
      setStep(2); // Go to Order Info step from Warehouse step
    } else {
      setStep(1); // For existing customer, go to Order Info (Step 1)
    }
  };

  const handleBack = () => setStep((prev) => Math.max(0, prev - 1));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;            // guard double-submit

    if (!validateStep()) {
      return;
    }


    setSubmitting(true);
    try {
      // Availability of each product in the selected warehouse is validated by
      // the backend on submit (it receives warehouse_id + product_size_ids). The
      // former client-side pre-check compared against selectedWarehouse.items,
      // which the /warehouses list never returns, so it flagged every item as
      // unavailable regardless of stock — removed.

      // Build payload
      if (mode === 'existing') {
        const payload = {
          customer_id: Number(order.customer_id),
          location_id: Number(order.location_id) || 0,
          freight: (order.freight !== '' && order.freight !== null) ? parseFloat(order.freight) : 0,
          freight_type: order.freight_type || null,
          freight_pricing_type: order.freight_pricing_type || 'fixed',
          loading_cost_per_kg: (order.loading_cost_per_kg !== '' && order.loading_cost_per_kg !== null) ? parseFloat(order.loading_cost_per_kg) : 0,
          price_list_date: order.price_list_date || null,
          expected_delivery_date: order.expected_delivery_date || null,
          include_gst: !!order.include_gst,
          remark: order.remark || '',
          items: (order.items || []).filter(item => item.product_size_id).map(item => ({
            product_size_id: Number(item.product_size_id),
            units: item.units || null,
            length: (item.length !== '' && item.length !== null) ? parseFloat(item.length) : 0,
            quantity: (item.quantity !== '' && item.quantity !== null) ? parseFloat(item.quantity) : 0,
            quantity_in_pieces: (item.quantity_in_pieces !== '' && item.quantity_in_pieces !== null && item.quantity_in_pieces !== undefined) ? parseInt(item.quantity_in_pieces, 10) : 0,
            // Coil cut instruction — sent only for coil-tracked lines, so ordinary
            // lines are untouched; the backend also drops it for non-coil SKUs.
            ...(isCoilTrackedLine(item) && item.cut_pieces ? { cut_pieces: parseInt(item.cut_pieces, 10) } : {}),
            price: (item.price !== '' && item.price !== null && item.price !== undefined) ? parseFloat(item.price) : null,
            remark: item.remark || '',
          })),
        };

        if (isConverting) {
          payload.source_type = order.source_type;
          payload.warehouse_id = order.selected_warehouse_id ? Number(order.selected_warehouse_id) : null;
          payload.supplier_id = order.selected_supplier_id ? Number(order.selected_supplier_id) : null;
        }

        // Close ONLY on success — onSubmit returns {success:false} on failure
        // (it doesn't throw), so a failed save must not close and drop the data.
        const result = await onSubmit(payload);
        if (!result || result.success !== true) throw new Error((result && result.error) || 'Failed to submit inquiry');
        onClose();
      } else {
        // new customer: submit customer object and order together
        const payload = {
          customer: {
            ...newCustomer,
            credit_period: Number(newCustomer.credit_period),
          },
          warehouses: (newCustomerWarehouses || []).map(wh => ({
            name: wh.name || '',
            location_id: Number(wh.location_id) || 0,
            country: wh.country || 'India',
            state: wh.state || '',
            city: wh.city || '',
            address: wh.address || '',
            map_url: wh.map_url || '',
            contact_person: wh.contact_person || '',
            contact_phone: wh.contact_phone || ''
          })),
          location_id: Number(order.location_id) || 0,
          freight: (order.freight !== '' && order.freight !== null) ? parseFloat(order.freight) : 0,
          freight_type: order.freight_type || null,
          freight_pricing_type: order.freight_pricing_type || 'fixed',
          loading_cost_per_kg: (order.loading_cost_per_kg !== '' && order.loading_cost_per_kg !== null) ? parseFloat(order.loading_cost_per_kg) : 0,
          price_list_date: order.price_list_date || null,
          expected_delivery_date: order.expected_delivery_date || null,
          include_gst: !!order.include_gst,
          remark: order.remark || '',
          items: (order.items || []).filter(item => item.product_size_id).map(item => ({
            product_size_id: Number(item.product_size_id),
            units: item.units || null,
            length: (item.length !== '' && item.length !== null) ? parseFloat(item.length) : 0,
            quantity: (item.quantity !== '' && item.quantity !== null) ? parseFloat(item.quantity) : 0,
            quantity_in_pieces: (item.quantity_in_pieces !== '' && item.quantity_in_pieces !== null && item.quantity_in_pieces !== undefined) ? parseInt(item.quantity_in_pieces, 10) : 0,
            // Coil cut instruction — sent only for coil-tracked lines, so ordinary
            // lines are untouched; the backend also drops it for non-coil SKUs.
            ...(isCoilTrackedLine(item) && item.cut_pieces ? { cut_pieces: parseInt(item.cut_pieces, 10) } : {}),
            price: (item.price !== '' && item.price !== null && item.price !== undefined) ? parseFloat(item.price) : null,
            remark: item.remark || '',
          })),
        };

        if (isConverting) {
          payload.source_type = order.source_type;
          payload.warehouse_id = order.selected_warehouse_id ? Number(order.selected_warehouse_id) : null;
          payload.supplier_id = order.selected_supplier_id ? Number(order.selected_supplier_id) : null;
        }

        const result = await onSubmit(payload);
        if (!result || result.success !== true) throw new Error((result && result.error) || 'Failed to submit inquiry');
        onClose();
      }
    } catch (submitError) {
      console.error('Error submitting inquiry form:', submitError);
      // Keep the modal open and surface the error so the entered data isn't lost.
      const msg = submitError?.message || 'Failed to submit inquiry. Please try again.';
      setErrors(prev => ({ ...prev, submit: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  // If the form isn't open, don't render UI
  if (!isOpen) return null;

  // Class used to hide native vertical scrollbar across browsers for the modal content
  const scrollClass = 'inquiry-form-scroll';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      {/* Inline style block to target webkit scrollbar pseudo-element for this component scope */}
      <style>{`
        .${scrollClass} { -ms-overflow-style: none; scrollbar-width: none; }
        .${scrollClass}::-webkit-scrollbar { display: none; }
      `}</style>
      <div className={`w-full max-w-6xl relative bg-white rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden ${scrollClass}`} style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{isConverting ? strings.title.convert : (currentInquiry || inquiry ? strings.title.edit : strings.title.add)}</h2>
          <button onClick={onClose} className="p-2 rounded-lg" style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 relative">
          {submitting && (
            <div className="absolute inset-0 z-40 flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.7)' }}>
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p className="text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{isConverting ? strings.loading?.converting : strings.loading?.submitting}</p>
              </div>
            </div>
          )}
          {inquiryLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p className="text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{strings.loading?.loadingDetails}</p>
              </div>
            </div>
          ) : (
            <>
              <div className={`flex-1 overflow-y-auto p-6 space-y-6 ${scrollClass}`}>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.mode.label}</label>
                  {/* If editing an inquiry, hide the 'new customer' option and lock to existing mode */}
                  {!(currentInquiry || inquiry) ? (
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2"><input type="radio" name="mode" checked={mode === 'existing'} onChange={() => setMode('existing')} /> {fields.mode.options.existing}</label>
                      <label className="flex items-center gap-2"><input type="radio" name="mode" checked={mode === 'new'} onChange={() => setMode('new')} /> {fields.mode.options.new}</label>
                    </div>
                  ) : (
                    <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{fields.mode.options.existing}</div>
                  )}
                </div>

                {mode === 'existing' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer_id.label} {REQUIRED}</label>
                        <SearchableSelect
                          name="customer_id"
                          value={order.customer_id}
                          onChange={handleOrderChange}
                          options={customers.map(c => ({ value: c.id, label: c.tally_name || c.company_name || c.owner_name || c.name || `Customer ${c.id}` }))}
                          placeholder={dropdownsLoading ? 'Loading customers...' : fields.customer_id.placeholder || 'Select a customer'}
                          disabled={isConverting || dropdownsLoading}
                          error={errors.customer_id}
                          onSearchChange={handleCustomerSearch}
                          loading={customerSearchLoading}
                          noOptionsMessage="Customer not found"
                        />
                        {errors.customer_id && (
                          <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.customer_id}</div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.location_id.label} {REQUIRED}</label>
                        <SearchableSelect
                          name="location_id"
                          value={order.location_id}
                          onChange={handleOrderChange}
                          options={deliveryLocationOptions}
                          placeholder={dropdownsLoading ? 'Loading locations...' : fields.location_id.placeholder || 'Select a location'}
                          // Disabled while resolving so a second click cannot create a
                          // duplicate delivery site for the same address.
                          disabled={isConverting || dropdownsLoading || resolving}
                          error={errors.location_id}
                          onSearchChange={handleLocationSearch}
                          loading={locationSearchLoading || resolving}
                          onAddNew={(term) => { setLocationAddInitialValue(term); setShowAddLocationModal(true); }}
                        />
                        {errors.location_id && (
                          <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.location_id}</div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.freightPricingType')}</label>
                        <SearchableSelect
                          options={[
                            { value: 'fixed', label: t('commonCols.statusOptions.fixed') },
                            { value: 'per_kg', label: t('commonCols.perKg') },
                          ]}
                          name="freight_pricing_type"
                          value={order.freight_pricing_type}
                          onChange={handleOrderChange}
                          placeholder={t('common.formFields.placeholders.selectFreightPricingType')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.freightType')}</label>
                        <select name="freight_type" value={order.freight_type} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}>
                          <option value="">{t('common.formFields.placeholders.selectFreightType')}</option>
                          <option value="ex_warehouse">{t('common.formFields.options.freightType.exWarehouse')}</option>
                          <option value="free_delivery_by_supplier">{t('common.formFields.options.freightType.freeDelivery')}</option>
                          <option value="freight_chargeable">{t('common.formFields.options.freightType.freightChargeable')}</option>
                          <option value="free_delivery_freight_to_pay">{t('common.formFields.options.freightType.freeDeliveryFreightToPay')}</option>
                          <option value="free_delivery_paid_by_company">{t('common.formFields.options.freightType.freeDeliveryPaidByCompany')}</option>
                        </select>
                      </div>
                      <div>
                        {order.freight_type !== 'free_delivery_by_supplier' && (
                          <>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                              {fields.freight.label}
                              {['ex_warehouse', 'free_delivery_paid_by_company'].includes(order.freight_type) && <span className="ml-1 text-xs font-normal text-gray-400">(Reference Only)</span>}
                            </label>
                            <input type="text" name="freight" value={order.freight} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.freight.placeholder} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.freight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                            {errors.freight && (
                              <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.freight}</div>
                            )}
                          </>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>Price List Date <span style={{ color: UI_COLORS.STATUS.ERROR }}>*</span></label>
                        <input type="date" name="price_list_date" value={order.price_list_date} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.price_list_date ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                        {errors.price_list_date && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.price_list_date}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.expected_delivery_date.label}</label>
                        <input type="date" lang="en-GB" name="expected_delivery_date" min={new Date().toLocaleDateString('en-CA')} value={order.expected_delivery_date} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.expected_delivery_date ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                        {errors.expected_delivery_date && (
                          <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.expected_delivery_date}</div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="flex items-center gap-2">
                          <input type="checkbox" name="include_gst" checked={!!order.include_gst} onChange={handleOrderChange} />
                          {fields.include_gst.label}
                        </label>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.referenceParty')}</label>
                        <input name="reference_party_name" value={order.reference_party_name} onChange={handleOrderChange} placeholder={t('common.formFields.placeholders.referenceParty')} className="w-full px-4 py-3 rounded-xl border-2" />
                      </div>
                    </div>

                    {isConverting && (
                      <div className="p-4 rounded-xl border-2 space-y-4" style={{ borderColor: UI_COLORS.BRAND.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.02) }}>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.sourceType.label} {REQUIRED}</label>
                            <select name="source_type" value={order.source_type} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ borderColor: errors.source_type ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }}>
                              <option value="">{fields.sourceType.placeholder}</option>
                              <option value="warehouse">{fields.sourceType.options.warehouse.label}</option>
                              <option value="supplier">{fields.sourceType.options.supplier.label}</option>
                            </select>
                            {errors.source_type && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.source_type}</p>}
                          </div>
                          <div>
                            {order.source_type === 'warehouse' ? (
                              <>
                                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.warehouse_id.label} {REQUIRED}</label>
                                <SearchableSelect
                                  name="selected_warehouse_id"
                                  value={order.selected_warehouse_id}
                                  onChange={handleOrderChange}
                                  options={warehouses.map(w => ({
                                    value: w.id,
                                    label: `${w.name || w.city} ${w.is_active ? '🟢' : '🔴'}`
                                  }))}
                                  placeholder={fields.warehouse_id.placeholder}
                                  error={errors.selected_warehouse_id}
                                  onSearchChange={handleWarehouseSearch}
                                  loading={warehouseSearchLoading}
                                />
                                {errors.selected_warehouse_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.selected_warehouse_id}</p>}
                              </>
                            ) : order.source_type === 'supplier' ? (
                              <>
                                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.supplier_id.label} {REQUIRED}</label>
                                <SearchableSelect
                                  name="selected_supplier_id"
                                  value={order.selected_supplier_id}
                                  onChange={handleOrderChange}
                                  options={suppliers.map(s => ({ value: s.id, label: s.company_name || s.owner_name || s.name }))}
                                  placeholder={fields.supplier_id.placeholder}
                                  error={errors.selected_supplier_id}
                                  onSearchChange={handleSupplierSearch}
                                  loading={supplierSearchLoading}
                                />
                                {errors.selected_supplier_id && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.selected_supplier_id}</p>}
                              </>
                            ) : null}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.loading_cost_per_kg.label}</label>
                          <input name="loading_cost_per_kg" value={order.loading_cost_per_kg} onChange={handleOrderChange} placeholder={fields.loading_cost_per_kg.placeholder} className="w-full px-4 py-3 rounded-xl border-2" />
                        </div>

                      </div>
                    )}

                    {/* Items editor */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-semibold">{fields.items?.label || 'Items'}</h4>
                      {order.items.map((item, idx) => {
                        return (
                          <div key={idx} className="rounded-xl border p-3">
                            <div className="grid gap-2 items-end" style={{ gridTemplateColumns: '3fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr auto' }}>
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.product_size_id.label} {REQUIRED}</label>
                                <SearchableSelect
                                  value={item.product_size_id}
                                  onChange={(e) => handleItemChange(idx, 'product_size_id', e.target.value)}
                                  options={(() => {
                                    const results = products || [];
                                    const currentId = String(item.product_size_id);
                                    if (!currentId) return results.map(p => ({ value: p.id, label: p.alias_name || p.product_name || p.name }));
                                    
                                    const isIdInResults = results.some(p => String(p.id) === currentId);
                                    if (isIdInResults) return results.map(p => ({ value: p.id, label: p.alias_name || p.product_name || p.name }));
                                    
                                    const currentItem = (cache.products || []).find(p => String(p.id) === currentId);
                                    
                                    const rowOptions = currentItem ? [currentItem, ...results] : results;
                                    return rowOptions.map(p => ({ 
                                      value: p.id || p.product_size_id, 
                                      label: (p.alias_name || p.product_name || p.name || p.size_name) 
                                    }));
                                  })()}
                                  placeholder={dropdownsLoading ? t('commonForm.loading') : t('commonForm.selectProduct')}
                                  error={errors[`items.${idx}.product_size_id`]}
                                  onSearchChange={(t) => handleProductSearch(idx, t)}
                                  loading={productSearchLoading.has(idx)}
                                  noOptionsMessage="Item not found"
                                />
                                {errors[`items.${idx}.product_size_id`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.product_size_id`]}</p>}
                              </div>
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.units.label}</label>
                                <select value={item.units} onChange={(e) => handleItemChange(idx, 'units', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background }}>
                                  {Object.entries({ kg: t('common.units.kg'), m: t('common.units.m'), pcs: t('common.units.pcs') }).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.quantity.label} {REQUIRED}</label>
                                <input value={item.quantity} onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)} placeholder={fields.quantity.placeholder} className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.quantity`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                {errors[`items.${idx}.quantity`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity`]}</p>}
                              </div>
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.pieces')}</label>
                                <input value={item.quantity_in_pieces} onChange={(e) => handleItemChange(idx, 'quantity_in_pieces', e.target.value)} placeholder="0" className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.quantity_in_pieces`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                {errors[`items.${idx}.quantity_in_pieces`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity_in_pieces`]}</p>}
                              </div>
                              {isCoilTrackedLine(item) && (
                                <div>
                                  <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('coils.line.cutPieces')}</label>
                                  <input value={item.cut_pieces} onChange={(e) => handleItemChange(idx, 'cut_pieces', e.target.value)} placeholder={t('coils.line.cutPieces')} title={t('coils.line.cutPiecesHint')} className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.cut_pieces`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                  {errors[`items.${idx}.cut_pieces`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.cut_pieces`]}</p>}
                                </div>
                              )}
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.price.label}</label>
                                <input value={item.price} onChange={(e) => handleItemChange(idx, 'price', e.target.value)} placeholder={fields.price.placeholder} className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.price`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                {errors[`items.${idx}.price`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.price`]}</p>}
                              </div>
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.length.label}</label>
                                <input value={item.length} onChange={(e) => handleItemChange(idx, 'length', e.target.value)} placeholder={fields.length.placeholder} className="w-full px-3 py-2 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                              </div>
                              <div className="flex items-end pb-0.5">
                                <button type="button" onClick={() => removeItem(idx)} className="p-2 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors">
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </div>
                            <div className="mt-2">
                              <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.remark.label}</label>
                              <input value={item.remark} onChange={(e) => handleItemChange(idx, 'remark', e.target.value)} placeholder={fields.remark.placeholder} className="w-full px-3 py-2 rounded-xl border-2 transition-all outline-none focus:border-yellow-400" style={{ backgroundColor: '#fef9c3', borderColor: UI_COLORS.FORMS.INPUT.border }} />
                            </div>
                          </div>
                        );
                      })}
                      <div>
                        <button type="button" onClick={addItem} className="px-4 py-2 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}><FontAwesomeIcon icon={faPlus} className="mr-2" />{strings.buttons.addItem}</button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.remark.label}</label>
                      <textarea name="remark" value={order.remark} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.remark.placeholder} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.remark ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                      {errors.remark && (
                        <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.remark}</div>
                      )}
                    </div>

                    {errors.submit && (
                      <div className="mt-3 p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>{errors.submit}</div>
                    )}
                    <div className="flex justify-end gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                      <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>{strings.buttons.cancel}</button>
                      <button type="submit" disabled={loading || submitting} className="px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                        <FontAwesomeIcon icon={faSave} className="mr-2" /> {strings.buttons.submit}
                      </button>
                    </div>
                  </div>
                )}

                {mode === 'new' && (
                  <div>
                    {step === 0 && (
                      <>
                        <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{strings.steps.customerInfo}</h3>
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.owner_name.label} {REQUIRED}</label>
                              <input name="owner_name" value={newCustomer.owner_name} onChange={e => handleCustomerChange(e)} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.owner_name.placeholder} style={{ borderColor: errors.owner_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.owner_name && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.owner_name}</div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.company_name.label} {REQUIRED}</label>
                              <input name="company_name" value={newCustomer.company_name} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.company_name.placeholder} style={{ borderColor: errors.company_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.company_name && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.company_name}</div>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.customer_type?.label || 'Customer Type'} {REQUIRED}</label>
                              <input name="customer_type" value={newCustomer.customer_type} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.customer_type?.placeholder || 'Customer'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.customer_type ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.customer_type && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.customer_type}</div>
                              )}
                            </div>
                            <div />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.email.label} {REQUIRED}</label>
                              <input name="email" value={newCustomer.email} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.email.placeholder} style={{ borderColor: errors.email ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.email && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.email}</div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.phone_number_1?.label || 'Mobile Number'} {REQUIRED}</label>
                              <input name="phone_number_1" value={newCustomer.phone_number_1} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.phone_number_1?.placeholder || 'Mobile Number'} style={{ borderColor: errors.phone_number_1 ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.phone_number_1 && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.phone_number_1}</div>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.referenceParty')}</label>
                                <input name="reference_party_name" value={newCustomer.reference_party_name} onChange={handleCustomerChange} placeholder={t('common.formFields.placeholders.referenceParty')} className="w-full px-4 py-3 rounded-xl border-2" />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.gst_number?.label || 'GST Number'} {REQUIRED}</label>
                              <input name="gst_number" value={newCustomer.gst_number} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.gst_number?.placeholder || 'GST Number'} style={{ borderColor: errors.gst_number ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.gst_number && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.gst_number}</div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.contact_person?.label || 'Contact Person'} {REQUIRED}</label>
                              <input name="contact_person" value={newCustomer.contact_person} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.contact_person?.placeholder || 'Contact Person'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.contact_person ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.contact_person && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.contact_person}</div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.address_line1?.label || 'Address'} {REQUIRED}</label>
                              <textarea name="address_line1" value={newCustomer.address_line1} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.address_line1?.placeholder || 'Address'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.address_line1 ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.address_line1 && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.address_line1}</div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.state?.label || 'State'} {REQUIRED}</label>
                              <SearchableSelect
                                name="state"
                                value={newCustomer.state}
                                onChange={handleCustomerChange}
                                options={t('locations.states', { returnObjects: true })}
                                placeholder={fields.customer.state?.placeholder || 'Select State'}
                                error={errors.state}
                              />
                              {errors.state && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.state}</div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.city?.label || 'City'} {REQUIRED}</label>
                              <input name="city" value={newCustomer.city} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.city?.placeholder || 'City'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.city ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.city && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.city}</div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.country?.label || 'Country'} {REQUIRED}</label>
                              <input name="country" value={newCustomer.country} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.country?.placeholder || 'Country'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.country ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.country && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.country}</div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.zip_code?.label || 'Zip Code'} {REQUIRED}</label>
                              <input name="zip_code" value={newCustomer.zip_code} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.zip_code?.placeholder || 'Zip Code'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.zip_code ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.zip_code && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.zip_code}</div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.tally_name?.label || 'Tally Name'}</label>
                              <input name="tally_name" value={newCustomer.tally_name} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.tally_name?.placeholder || 'Tally Name'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.tally_name ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.tally_name && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.tally_name}</div>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.customer.credit_period?.label || 'Credit Period (days)'}</label>
                              <input name="credit_period" type="number" value={newCustomer.credit_period} onChange={handleCustomerChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.customer.credit_period?.placeholder || '30'} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.credit_period ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.credit_period && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.credit_period}</div>
                              )}
                            </div>
                          </div>
                          {/* minimal fields shown; more can be added similarly */}
                          <div className="flex justify-end gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                            <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>{strings.buttons.cancel}</button>
                            <button type="button" onClick={handleNext} className="px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                              {strings.buttons.next} <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}

                    {step === 1 && mode === 'new' && (
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('common.formFields.labels.warehouseInformation')}</h3>
                          <button
                            type="button"
                            onClick={() => setShowWarehouseModal(true)}
                            className="px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:scale-105"
                            style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.1), color: UI_COLORS.BRAND.PRIMARY }}
                          >
                            <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add Warehouse
                          </button>
                        </div>

                        <p className="text-sm mb-4" style={{ color: UI_COLORS.TEXT.MUTED }}>
                          You can add one or more warehouses for this new customer now, or skip this step.
                        </p>

                        <div className="space-y-3 min-h-[200px]">
                          {newCustomerWarehouses.length === 0 ? (
                            <div className="flex flex-col items-center justify-center p-8 rounded-2xl border-2 border-dashed" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                              <FontAwesomeIcon icon={faWarehouse} className="text-3xl mb-2" style={{ color: UI_COLORS.TEXT.MUTED }} />
                              <p className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.MUTED }}>No warehouses added yet</p>
                              <p className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>Warehouses are optional</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {newCustomerWarehouses.map((wh, idx) => (
                                <div key={idx} className="p-4 rounded-xl border relative group" style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.SECONDARY }}>
                                  <button
                                    type="button"
                                    onClick={() => setNewCustomerWarehouses(prev => {
                                      const filtered = prev.filter((_, i) => i !== idx);
                                      if (filtered.length > 0 && !filtered.some(w => w.is_default)) {
                                        filtered[0].is_default = true;
                                      }
                                      return filtered;
                                    })}
                                    className="absolute top-2 right-2 p-1.5 rounded-lg text-red-500 hover:bg-red-50 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-all"
                                  >
                                    <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                  </button>
                                  <div className="flex items-start gap-3">
                                    <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-500">
                                      <FontAwesomeIcon icon={faWarehouse} className="text-sm" />
                                    </div>
                                    <div className="overflow-hidden">
                                      <div className="flex items-center gap-2 overflow-hidden">
                                        <h4 className="font-semibold text-sm truncate" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{wh.name}</h4>
                                        {wh.is_default && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-bold uppercase tracking-wider flex-shrink-0">Default</span>
                                        )}
                                      </div>
                                      <p className="text-xs mt-0.5 truncate" style={{ color: UI_COLORS.TEXT.MUTED }}>{wh.address || wh.city || 'No address provided'}</p>
                                      {!wh.is_default && (
                                        <button
                                          type="button"
                                          onClick={() => setNewCustomerWarehouses(prev => prev.map((w, i) => ({ ...w, is_default: i === idx })))}
                                          className="text-[10px] mt-1 text-blue-600 hover:underline font-semibold"
                                        >
                                          Set as Default
                                        </button>
                                      )}
                                      {wh.contact_person && (
                                        <p className="text-[10px] mt-1 font-medium" style={{ color: UI_COLORS.BRAND.PRIMARY }}>
                                          Contact: {wh.contact_person}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-4 mt-8 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                          <button type="button" onClick={handleBack} className="px-6 py-3 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>
                            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> {strings.buttons.back}
                          </button>
                          <button type="button" onClick={handleNext} className="ml-auto px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>
                            {newCustomerWarehouses.length === 0 ? t('commonForm.skipAndNext') : strings.buttons.next} <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                          </button>
                        </div>
                      </>
                    )}

                    {((step === 1 && mode === 'existing') || (step === 2 && mode === 'new')) && (
                      <>
                        <h3 className="text-lg font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{strings.steps.orderInfo}</h3>
                        {(newCustomer.owner_name || newCustomer.company_name) && (
                          <div className="flex items-center gap-4 px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.06) }}>
                            {newCustomer.owner_name && (
                              <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>Customer: <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{newCustomer.owner_name}</span></span>
                            )}
                            {newCustomer.company_name && (
                              <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>Company: <span className="font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{newCustomer.company_name}</span></span>
                            )}
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.location_id.label} {REQUIRED}</label>
                            <SearchableSelect
                              name="location_id"
                              value={order.location_id}
                              onChange={handleOrderChange}
                              // The offline warehouses used to be listed here under
                              // `new_wh_${idx}` sentinels, which submit coerced to 0.
                              // The shared builder lists them by their real location id.
                              options={deliveryLocationOptions}
                              placeholder={dropdownsLoading ? 'Loading locations...' : fields.location_id.placeholder || 'Select a location'}
                              // Disabled while resolving so a second click cannot create
                              // a duplicate delivery site for the same address.
                              disabled={dropdownsLoading || resolving}
                              error={errors.location_id}
                              onSearchChange={handleLocationSearch}
                              loading={locationSearchLoading || resolving}
                              onAddNew={(term) => { setLocationAddInitialValue(term); setShowAddLocationModal(true); }}
                            />
                            {errors.location_id && (
                              <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.location_id}</div>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>Price List Date <span style={{ color: UI_COLORS.STATUS.ERROR }}>*</span></label>
                            <input type="date" name="price_list_date" value={order.price_list_date} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.price_list_date ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                            {errors.price_list_date && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.price_list_date}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.expected_delivery_date.label}</label>
                            <input type="date" name="expected_delivery_date" min={new Date().toLocaleDateString('en-CA')} value={order.expected_delivery_date} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.expected_delivery_date ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                            {errors.expected_delivery_date && (
                              <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.expected_delivery_date}</div>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.freightPricingType')}</label>
                            <SearchableSelect
                              options={[
                                { value: 'fixed', label: t('commonCols.statusOptions.fixed') },
                                { value: 'per_kg', label: t('commonCols.perKg') },
                              ]}
                              name="freight_pricing_type"
                              value={order.freight_pricing_type}
                              onChange={handleOrderChange}
                              placeholder={t('common.formFields.placeholders.selectFreightPricingType')}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.freightType')}</label>
                            <select name="freight_type" value={order.freight_type} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}>
                              <option value="">{t('common.formFields.placeholders.selectFreightType')}</option>
                              <option value="ex_warehouse">{t('common.formFields.options.freightType.exWarehouse')}</option>
                              <option value="free_delivery_by_supplier">{t('common.formFields.options.freightType.freeDelivery')}</option>
                              <option value="freight_chargeable">{t('common.formFields.options.freightType.freightChargeable')}</option>
                              <option value="free_delivery_freight_to_pay">{t('common.formFields.options.freightType.freeDeliveryFreightToPay')}</option>
                              <option value="free_delivery_paid_by_company">{t('common.formFields.options.freightType.freeDeliveryPaidByCompany')}</option>
                            </select>
                          </div>
                          <div>
                            <label className="flex items-center gap-2">
                              <input type="checkbox" name="include_gst" checked={!!order.include_gst} onChange={handleOrderChange} />
                              {fields.include_gst.label}
                            </label>
                          </div>
                          <div />
                        </div>
                        {order.freight_type !== 'free_delivery_by_supplier' && (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                {fields.freight.label}
                                {['ex_warehouse', 'free_delivery_paid_by_company'].includes(order.freight_type) && <span className="ml-1 text-xs font-normal text-gray-400">(Reference Only)</span>}
                              </label>
                              <input name="freight" value={order.freight} onChange={handleOrderChange} placeholder={fields.freight.placeholder} className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.freight ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                              {errors.freight && (
                                <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.freight}</div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Items editor for new customer flow */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-semibold">{fields.items?.label || 'Items'}</h4>
                          {order.items.map((item, idx) => {

                            return (
                              <div key={idx} className="rounded-xl border p-3">
                                <div className="grid gap-2 items-end" style={{ gridTemplateColumns: '3fr 0.8fr 0.8fr 0.8fr 0.8fr 0.8fr auto' }}>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.product_size_id.label} {REQUIRED}</label>
                                    <SearchableSelect
                                      value={item.product_size_id}
                                      onChange={(e) => handleItemChange(idx, 'product_size_id', e.target.value)}
                                      options={(() => {
                                        const results = products || [];
                                        const currentId = String(item.product_size_id);
                                        if (!currentId) return results.map(p => ({ value: p.id, label: p.alias_name || p.product_name || p.name }));
                                        
                                        const isIdInResults = results.some(p => String(p.id) === currentId);
                                        if (isIdInResults) return results.map(p => ({ value: p.id, label: p.alias_name || p.product_name || p.name }));
                                        
                                        const currentItem = (cache.products || []).find(p => String(p.id) === currentId);
                                        
                                        const rowOptions = currentItem ? [currentItem, ...results] : results;
                                        return rowOptions.map(p => ({ 
                                          value: p.id || p.product_size_id, 
                                          label: (p.alias_name || p.product_name || p.name || p.size_name) 
                                        }));
                                      })()}
                                      placeholder={dropdownsLoading ? t('commonForm.loading') : t('commonForm.selectProduct')}
                                      error={errors[`items.${idx}.product_size_id`]}
                                      onSearchChange={(t) => handleProductSearch(idx, t)}
                                      loading={productSearchLoading.has(idx)}
                                    />
                                    {errors[`items.${idx}.product_size_id`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.product_size_id`]}</p>}
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.units.label}</label>
                                    <select value={item.units} onChange={(e) => handleItemChange(idx, 'units', e.target.value)} className="w-full px-3 py-2 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background }}>
                                      {Object.entries({ kg: t('common.units.kg'), m: t('common.units.m'), pcs: t('common.units.pcs') }).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.quantity.label} {REQUIRED}</label>
                                    <input value={item.quantity} onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)} placeholder={fields.quantity.placeholder} className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.quantity`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                    {errors[`items.${idx}.quantity`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity`]}</p>}
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.pieces')}</label>
                                    <input value={item.quantity_in_pieces} onChange={(e) => handleItemChange(idx, 'quantity_in_pieces', e.target.value)} placeholder="0" className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.quantity_in_pieces`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                    {errors[`items.${idx}.quantity_in_pieces`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.quantity_in_pieces`]}</p>}
                                  </div>
                                  {isCoilTrackedLine(item) && (
                                    <div>
                                      <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('coils.line.cutPieces')}</label>
                                      <input value={item.cut_pieces} onChange={(e) => handleItemChange(idx, 'cut_pieces', e.target.value)} placeholder={t('coils.line.cutPieces')} title={t('coils.line.cutPiecesHint')} className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.cut_pieces`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                      {errors[`items.${idx}.cut_pieces`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.cut_pieces`]}</p>}
                                    </div>
                                  )}
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.price.label}</label>
                                    <input value={item.price} onChange={(e) => handleItemChange(idx, 'price', e.target.value)} placeholder={fields.price.placeholder} className="w-full px-3 py-2 rounded-xl border-2" style={{ borderColor: errors[`items.${idx}.price`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                    {errors[`items.${idx}.price`] && <p className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`items.${idx}.price`]}</p>}
                                  </div>
                                  <div>
                                    <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.length.label}</label>
                                    <input value={item.length} onChange={(e) => handleItemChange(idx, 'length', e.target.value)} placeholder={fields.length.placeholder} className="w-full px-3 py-2 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background }} />
                                  </div>
                                  <div className="flex items-end pb-0.5">
                                    <button type="button" onClick={() => removeItem(idx)} className="p-2 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors">
                                      <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                  </div>
                                </div>
                                <div className="mt-2">
                                  <label className="block text-xs font-semibold mb-1" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.remark.label}</label>
                                  <input value={item.remark} onChange={(e) => handleItemChange(idx, 'remark', e.target.value)} placeholder={fields.remark.placeholder} className="w-full px-3 py-2 rounded-xl border-2 transition-all outline-none focus:border-yellow-400" style={{ backgroundColor: '#fef9c3', borderColor: UI_COLORS.FORMS.INPUT.border }} />
                                </div>
                              </div>
                            );
                          })}
                          <div>
                            <button type="button" onClick={addItem} className="px-4 py-2 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}><FontAwesomeIcon icon={faPlus} className="mr-2" />Add Item</button>
                          </div>
                        </div>



                        <div>
                          <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>{fields.remark.label}</label>
                          <textarea name="remark" value={order.remark} onChange={handleOrderChange} className="w-full px-4 py-3 rounded-xl border-2" placeholder={fields.remark.placeholder} style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: errors.remark ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border }} />
                          {errors.remark && (
                            <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.remark}</div>
                          )}
                        </div>

                        {errors.submit && (
                          <div className="mt-3 p-3 rounded-lg text-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>{errors.submit}</div>
                        )}
                        <div className="flex items-center gap-4 mt-4">
                          <button type="button" onClick={handleBack} className="px-4 py-2 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>
                            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> {strings.buttons.back}
                          </button>
                          <div className="ml-auto flex items-center gap-4">
                            <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl" style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}>{strings.buttons.cancel}</button>
                            <button type="submit" disabled={loading || submitting} className="px-6 py-3 rounded-xl font-semibold" style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}>{isConverting ? strings.buttons.convert : strings.buttons.submit}</button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </form>

        <AddLocationModal
          isOpen={showAddLocationModal}
          onClose={() => { setShowAddLocationModal(false); setLocationAddInitialValue(''); }}
          onSuccess={handleAddLocationSuccess}
          initialValues={{ delivery_location: locationAddInitialValue }}
        />

        {/* Completes the customer's address before it becomes a delivery site. Prefilled
            from their record; the missing part (almost always city) is typed once and is
            then remembered as a saved site. Offline so nothing is written until Save. */}
        <CustomerWarehouseForm
          isOpen={showAddressPrompt}
          onClose={() => setShowAddressPrompt(false)}
          isOffline={true}
          initialData={addressPromptInitialData}
          onSave={async (data) => {
            setShowAddressPrompt(false);
            // The modal also offers "use an existing location". Picking that returns a
            // location_id with the address fields left blank, so there is nothing to
            // resolve — just select it.
            if (data.location_id) {
              const picked = locations.find(l => String(l.id) === String(data.location_id));
              handleAddLocationSuccess(picked || { id: data.location_id });
              return;
            }
            await commitCustomerAddress(
              {
                materializable: true,
                delivery_location: data.address,
                city: data.city,
                state: data.state,
                country: data.country || 'India',
              },
              data.name
            );
          }}
        />

        <CustomerWarehouseForm
          isOpen={showWarehouseModal}
          onClose={() => setShowWarehouseModal(false)}
          isOffline={true}
          onSave={async (data) => {
            let locationId = data.location_id;

            // If it's a new location (not selected from existing), create it via API
            if (!locationId) {
              try {
                const locRes = await locationsService.createLocation({
                  delivery_location: (data.address && data.address.trim().length >= 5) 
                    ? data.address.trim() 
                    : `${data.name} - ${data.address || data.city || ''}`.trim(),
                  city: data.city,
                  state: data.state,
                  country: data.country || 'India',
                  map_url: data.map_url,
                  is_active: true
                });

                if (locRes.success && locRes.data?.id) {
                  locationId = locRes.data.id;
                }
              } catch (err) {
                console.error("Failed to create location for warehouse:", err);
              }
            }

            const updatedWarehouse = { ...data, location_id: locationId };
            setNewCustomerWarehouses(prev => {
              const isFirst = prev.length === 0;
              const shouldBeDefault = isFirst || updatedWarehouse.is_default;
              const newWh = { ...updatedWarehouse, is_default: shouldBeDefault };
              if (shouldBeDefault) {
                return [...prev.map(w => ({ ...w, is_default: false })), newWh];
              }
              return [...prev, newWh];
            });

            // Automatically use this location as the delivery location for the inquiry
            if (locationId) {
              setOrder(prev => ({ ...prev, location_id: locationId }));
            }
          }}
        />
      </div>
    </div >
  );
};

export default InquiryForm;
